/**
 * @module features/agent-evals/agentEvalPresentation
 *
 * Verdict badge and number formatting for the agent eval run page. The values match the CLI markdown
 * (`meanTaskTokens` is shared; the CLI abbreviates tokens as `k`, the page prints them in full); costs keep two significant digits because per-attempt costs are cents-scale.
 */
import type { TagColor } from '@chatsdet/ui';
import type { AgentEvalRunResult, AgentEvalTokens } from '@chatsdet/types/agent-eval-result';

type Presentation = { label: string; color: TagColor };

const STATUSES: Record<AgentEvalRunResult['status'], Presentation> = {
  queued: { label: 'Queued', color: 'gray' },
  running: { label: 'Running', color: 'blue' },
  completed: { label: 'Completed', color: 'green' },
  failed: { label: 'Failed', color: 'red' },
  cancelled: { label: 'Cancelled', color: 'gray' },
};

/** PASS / FAIL once the run has a verdict; before that, its status. */
export const runVerdictPresentation = (run: {
  verdict: AgentEvalRunResult['verdict'];
  status: AgentEvalRunResult['status'];
}): Presentation => {
  if (run.verdict === 'pass') return { label: 'PASS', color: 'green' };
  if (run.verdict === 'fail') return { label: 'FAIL', color: 'red' };
  return STATUSES[run.status];
};

/**
 * Dollars from cents; a null cost is an unpriced model, never zero. `incomplete` marks a cost that omits calls with incomplete usage, like the CLI's trailing `*`.
 */
export const formatCostCents = (cents: number | null, incomplete = false): string => {
  if (cents === null) return 'unpriced';
  const dollars = cents / 100;
  // Cents-scale costs need two significant digits to be readable; a plain 2-decimal format would show $0.00.
  const text = dollars === 0 || dollars >= 0.1 ? `$${dollars.toFixed(2)}` : `$${Number(dollars.toPrecision(2))}`;
  return incomplete ? `${text}*` : text;
};

export const formatTokens = (tokens: AgentEvalTokens): string =>
  `${tokens.input.toLocaleString('en-US')} in / ${tokens.output.toLocaleString('en-US')} out`;

export const formatPercent = (fraction: number): string => `${Math.round(fraction * 100)}%`;

export const shortSha = (sha: string): string => sha.slice(0, 7);
